/**
 * The rules of getting paid, in one place, shared by the Worker and the screens.
 *
 * Pure values and pure functions only — no zod, no I/O — because this module is
 * re-exported from the package barrel and therefore ships to the browser. The
 * request/response SHAPES live in `./contracts.ts`, which does not.
 */

/**
 * The floor below which a payout request is refused, in cents.
 *
 * CAMPAIGN-INDEPENDENT on purpose. A minimum that varied per campaign would
 * mean a creator with $12 on one campaign and $12 on another could be told
 * both that they cannot be paid, while the money owed to them — which is one
 * number, because a payout is against the creator and not against a campaign —
 * is $24. The overridable value is the env var `PAYOUT_MINIMUM_CENTS`.
 */
export const PAYOUT_MINIMUM_CENTS_DEFAULT = 2_000;

/**
 * Read the minimum from configuration.
 *
 * A missing, empty, non-numeric, negative or fractional value falls back to the
 * default rather than to zero: a typo in a Worker var must never be the thing
 * that lets a one-cent payout into a batch a person has to pay by hand.
 */
export function payoutMinimumCents(raw: string | undefined | null): number {
  const value = Number((raw ?? '').trim());
  if (!Number.isInteger(value) || value < 1) return PAYOUT_MINIMUM_CENTS_DEFAULT;
  return value;
}

/**
 * How the creator is actually paid, on the MANUAL rail.
 *
 * The kind is stored in clear (it is not a secret, and the operator's queue has
 * to be groupable by it); the destination itself — the PayPal address, the bank
 * details — is encrypted at rest under `TOKEN_KEK`, the same way an OAuth
 * refresh token is, and is decrypted at exactly one place: the operator's
 * payout instructions.
 */
/**
 * `USDT` is a method KIND and not a provider: `CreatorPayoutProfile.provider`
 * is CHECK-pinned to MANUAL / STRIPE_CONNECT / PAYPAL by migration 0000 and
 * cannot be widened on D1 without a rebuild, while `methodKind` (added by 0005)
 * has no constraint at all. A USDT payout is therefore `provider = MANUAL,
 * methodKind = USDT` — the operator sends it by hand, exactly like a PayPal
 * one — with the network in `cryptoNetwork` and the address sealed in the
 * same `methodCiphertext` the PayPal email uses. See docs/schema.md.
 */
export const PAYOUT_METHOD_KINDS = ['PAYPAL', 'USDT', 'BANK_TRANSFER', 'WISE', 'OTHER'] as const;
export type PayoutMethodKind = (typeof PAYOUT_METHOD_KINDS)[number];

export const PAYOUT_METHOD_LABELS: Record<PayoutMethodKind, string> = {
  PAYPAL: 'PayPal',
  USDT: 'USDT (crypto)',
  BANK_TRANSFER: 'Bank transfer',
  WISE: 'Wise',
  OTHER: 'Something else',
};

/**
 * The non-secret hint stored beside the ciphertext, and the only form of the
 * payout destination that any screen, log line or Telegram message ever sees.
 *
 * `grace@example.com` becomes `gr…@example.com`; `GB29 NWBK 6016 1331 9268 19`
 * becomes `…9268 19`. Enough for a creator to recognise their own details and
 * for the operator to tell two rows apart, and not enough to pay the wrong
 * person or to be worth stealing out of a log.
 */
export function maskPayoutMethod(text: string): string {
  const value = text.trim();
  if (value.length === 0) return '';
  const at = value.indexOf('@');
  if (at > 0 && at < value.length - 1) {
    const local = value.slice(0, at);
    const domain = value.slice(at);
    const head = local.slice(0, Math.min(2, local.length));
    return `${head}${local.length > 2 ? '…' : ''}${domain}`;
  }
  const tail = value.slice(-4);
  return `…${tail}`;
}

/**
 * The sentence a creator reads next to money that is theirs but not yet
 * movable. Written here so the wallet, the admin queue and any later email all
 * say the same thing about the same row.
 */
export function pendingReasonSentence(input: {
  heldReason: string | null;
  heldCheck: string | null;
  payableAfter: string;
  now: Date;
}): string {
  if (input.heldReason) return input.heldReason;
  if (input.heldCheck) return `Held by the ${input.heldCheck.replace(/_/g, ' ').toLowerCase()} check.`;
  const at = Date.parse(input.payableAfter);
  if (Number.isFinite(at) && at > input.now.getTime()) {
    return `Inside the holdback until ${input.payableAfter.slice(0, 10)}.`;
  }
  return 'Not payable yet.';
}

/**
 * A payout that has been ASKED FOR but not yet approved.
 *
 * `PAYOUT_STATUSES` is `DRAFT | APPROVED | PROCESSING | PAID | FAILED |
 * REVERSED | CANCELLED`, pinned by a CHECK constraint ported from the scaffold,
 * and SQLite cannot add `REQUESTED` to one without rebuilding a live table —
 * which migration 0002 proved deletes its children on D1. So "requested" is
 * `status = 'DRAFT' AND requestedAt IS NOT NULL`, exactly as "held" is
 * `heldAt IS NOT NULL` on an earning, and this function is the one definition.
 */
export function payoutIsRequested(payout: { status: string; requestedAt: string | null }): boolean {
  return payout.status === 'DRAFT' && payout.requestedAt !== null;
}

/** Statuses in which a payout still has a claim on its earnings. */
export const PAYOUT_OPEN_STATUSES = ['DRAFT', 'APPROVED', 'PROCESSING'] as const;

export function payoutIsOpen(payout: { status: string }): boolean {
  return (PAYOUT_OPEN_STATUSES as readonly string[]).includes(payout.status);
}

/**
 * The key that groups one weekly run, derived from the local date so that a
 * second run on the same Monday finds the batch it already made rather than
 * cutting a second one. `weekly-2026-09-21`.
 */
export function weeklyBatchKey(localDate: string): string {
  return `weekly-${localDate}`;
}

/** Fraction of a campaign's funded budget at which the operator is warned. */
export const CAMPAIGN_BUDGET_ALERT_FRACTION = 0.8;

/* ------------------------------------------------------------------ */
/* Earnings over time                                                  */
/* ------------------------------------------------------------------ */

/**
 * The most days one `GET /wallet/earnings-series` request may ask for.
 *
 * The series is zero-filled — a week with nothing in it has to be a flat
 * stretch and not a missing one — so the day count IS the response size, and a
 * custom range of "1970 to now" would be 20,000 points. It lives here rather
 * than in `contracts.ts` because the Wallet's date inputs have to refuse the
 * range BEFORE asking, with a sentence, rather than showing the API's 400.
 */
export const EARNINGS_SERIES_MAX_DAYS = 400;

/** The preset ranges the Wallet offers, and what each one is called. */
export const EARNINGS_RANGES = ['7d', '30d', '90d', 'all', 'custom'] as const;
export type EarningsRange = (typeof EARNINGS_RANGES)[number];

export const EARNINGS_RANGE_LABELS: Readonly<Record<EarningsRange, string>> = Object.freeze({
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  all: 'All time',
  custom: 'Custom',
});

/** How many days back each preset starts, counting today as one of them. */
export const EARNINGS_RANGE_DAYS: Readonly<Record<'7d' | '30d' | '90d', number>> = Object.freeze({
  '7d': 7,
  '30d': 30,
  '90d': 90,
});

/** `YYYY-MM-DD` for an instant, in UTC. The bucket key everything here uses. */
export function utcDay(at: Date | string): string {
  const date = typeof at === 'string' ? new Date(at) : at;
  return date.toISOString().slice(0, 10);
}

/** One day added to (or subtracted from) a `YYYY-MM-DD`, still in UTC. */
export function addDays(day: string, delta: number): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + delta);
  return at.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to`, inclusive of both ends. 0 when reversed. */
export function daysInclusive(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00.000Z`);
  const b = Date.parse(`${to}T00:00:00.000Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
  return Math.round((b - a) / 86_400_000) + 1;
}
